import { injectReducer } from 'store/reducers.js'
import update from 'immutability-helper'
import { get } from 'lodash'

export function generateTypes (name) {
  name = name.toUpperCase()
  return [
    getType(name, 'request'),
    getType(name, 'success'),
    getType(name, 'failure')
  ]
}

export function getType (name, status) {
  name = name.toUpperCase()
  status = status.toUpperCase()

  return `${name}_${status}`
}

export function getModelActionHandlers ({
  idField,
  modelName,
  initialState
}) {
  idField = idField || 'id'
  initialState = initialState || {}

  return {
    [getType(modelName, 'success')]: (state, action) => {
      return action.payload
    },
    [getType(`create_${modelName}`, 'success')]: (state, action) => {
      return action.payload
    },
    [getType(`update_${modelName}`, 'success')]: (state, action) => {
      return action.payload
    },
    [getType(`destroy_${modelName}`, 'success')]: (state, action) => {
      if (state[idField] === action.id) state = initialState
      return state
    }
  }
}

export function getCollectionActionHandlers ({
  idField,
  modelName, modelPlural,
  initialState, initialModelState,
  ...options
}) {
  options = options || {}
  idField = idField || 'id'
  modelPlural = modelPlural || `${modelName}s`
  initialState = initialState || []
  initialModelState = initialModelState || {}

  let getTempId = (model) => {
    if (typeof options.getTempId === 'function') return options.getTempId(model)
    return get(model, options.tempId)
  }

  return {
    /** Model Methods for Collection State **/
    [getType(modelPlural, 'success')]: (state, action) => {
      return action.payload
    },

    /** Model Methods for Collection State **/
    /* ON CREATE / POST */
    [getType(`create_${modelName}`, 'request')]: (state, action) => {
      if (action.create_temp === false || !getTempId(action.data)) return state
      let tempModel = Object.assign({ __temp_id: getTempId(action.data) }, action.data)

      // filter out this tempModel, then push it into the state
      return update(
        state && state.filter(model => !model.__temp_id || model.__temp_id !== getTempId(action.payload)),
        { $push: [tempModel] }
      )
    },
    [getType(`create_${modelName}`, 'success')]: (state, action) => {
      return update(
        // filter out the created tempModel, then push the result into the state
        state && state.filter(model => !model.__temp_id || model.__temp_id !== getTempId(action.payload)),
        { $push: [action.payload] }
      )
    },
    [getType(`create_${modelName}`, 'failure')]: (state, action) => {
      return state && state.filter(model => !model.__temp_id || model.__temp_id !== getTempId(action.payload)) ||
        initialState
    },
    [getType(`create_multiple_${modelName}s`, 'request')]: (state = initialState, action) => {
      if (action.create_temp === false) return state

      const tempIds = action.data.map(model => getTempId(model))

      let data = action.data.map(data => Object.assign({ __temp_id: getTempId(data) }, data))

      return update(
        state && state.filter(model => !model.__temp_id || !tempIds.includes(model.__temp_id)),
        { $push: data }
      )
    },
    [getType(`create_multiple_${modelName}s`, 'success')]: (state = initialState, action) => {
      const tempIds = action.data.map(model => getTempId(model)) // use the original tempId instead!

      return update(
        state && state.filter(model => !model.__temp_id || !tempIds.includes(model.__temp_id)),
        { $push: action.payload }
      )
    },
    [getType(`create_multiple_${modelName}s`, 'failure')]: (state = initialState, action) => {
      const tempIds = action.data.map(model => getTempId(model))

      return state && state.filter(model => !model.__temp_id || !tempIds.includes(model.__temp_id)) ||
        initialState
    },

    /* ON UPDATE / PUT */
    [getType(`put_${modelName}`, 'request')]: (state, action) => {
      let foundIndex = state.findIndex(model => model[idField] === action.id)
      if (foundIndex < 0 || !state[foundIndex]) return state

      const merged = Object.assign({ __original_model: state[foundIndex] }, state[foundIndex], action.data)

      // update the foundIndex in the state with the sent action.data
      return update(
        state,
        { [foundIndex]: { $set: merged } }
      )
    },
    [getType(`put_${modelName}`, 'success')]: (state, action) => {
      let foundIndex = state.findIndex(model => model[idField] === action.id)
      if (foundIndex < 0 || !state[foundIndex]) return state

      return update(
        state,
        { [foundIndex]: { $set: action.payload } }
      )
    },
    [getType(`put_${modelName}`, 'failure')]: (state, action) => {
      let foundIndex = state.findIndex(model => model[idField] === action.id)
      if (foundIndex < 0 || !state[foundIndex]) return state

      // revert the foundIndex in the state with the original data (hopefully saved in the state previously)
      return update(
        state,
        { [foundIndex]: { $set: state[foundIndex].__original_model || state[foundIndex] } }
      )
    },
    [getType(`put_multiple_${modelPlural}`, 'request')]: (state, action) => {
      let updates = {}

      action.data.forEach(data => {
        let foundIndex = state.findIndex(model => model[idField] === data[idField])
        if (foundIndex < 0 || !state[foundIndex]) return

        const merged = Object.assign({ __original_model: state[foundIndex] }, state[foundIndex], data)

        updates[foundIndex] = { $set: merged }
      })

      return update(
        state,
        updates
      )
    },
    [getType(`put_multiple_${modelPlural}`, 'success')]: (state, action) => {
      let updates = {}

      action.payload.forEach(data => {
        let foundIndex = state.findIndex(model => model[idField] === data[idField])
        if (foundIndex < 0 || !state[foundIndex]) return

        const merged = Object.assign({}, state[foundIndex], data)

        updates[foundIndex] = { $set: merged }
      })

      return update(
        state,
        updates
      )
    },
    [getType(`put_multiple_${modelPlural}`, 'failure')]: (state, action) => {
      let updates = {}

      action.data.forEach(data => {
        let foundIndex = state.findIndex(model => model[idField] === data[idField])
        if (foundIndex < 0 || !state[foundIndex]) return

        updates[foundIndex] = { $set: state[foundIndex].__original_model || state[foundIndex] }
      })

      return update(
        state,
        updates
      )
    },

    /* ON DELETE */
    [getType(`destroy_${modelName}`, 'request')]: (state, action) => {
      return state && state.filter(model => model[idField] !== action.id)
    },
    [getType(`destroy_${modelName}`, 'success')]: (state, action) => {
      return state && state.filter(model => model[idField] !== action.id)
    }
  }
}

const getExternalModule = (name) => {
  name = name.toLowerCase()
  let parentName = name.capitalize()
  if (name === 'pricelist') parentName = 'DailyProducts'
  if (name === 'staging') parentName = 'Orders'

  return require(`../routes/${parentName}/modules/${name}`)
}

export const getExternalActions = (name) => {
  return getExternalModule(name).actions
}

export const getExternalReducer = (name) => {
  return getExternalModule(name).default
}

export const injectExternalList = (store, name, args) => {
  name = name.toLowerCase()

  const actions = getExternalActions(name)
  const reducer = getExternalReducer(name)

  injectReducer(store, { key: name, reducer })

  args = Array.isArray(args) ? args : [args]

  return store.dispatch(actions.get(...args))
}

export const injectExternalReducer = (store, name) => {
  name = name.toLowerCase()

  const reducer = getExternalReducer(name)

  injectReducer(store, { key: name, reducer })
}



// WEBPACK FOOTER //
// ./src/helpers/actions.js